import React, { PropsWithChildren } from 'react';
import { Box, Paper, Popper, PopperProps } from '@mui/material';

// This approach of rendering a popper with arrow was inspired by https://codesandbox.io/s/popper-with-arrow-58jhe.
// It has been heavily modified for our use case, and made to work with MUI 5 based on the updated styles from
//   https://github.com/mui/material-ui/blob/d28f2f69340a1e7458d71897ed90883b57d83463/packages/mui-material/src/Tooltip/Tooltip.js

export function PopperWithArrow(props: PropsWithChildren<PopperProps & Required<Pick<PopperProps, 'placement'>>>) {
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);

  return (
    <Popper
      {...props}
      sx={[
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        {
          '&[data-popper-placement*="left"] .MuiPopper-arrow': {
            right: 0,
            marginRight: '-0.71em',
            height: '1em',
            width: '0.71em',
            '&::before': {
              transformOrigin: '0 0',
            },
          },
          '&[data-popper-placement*="right"] .MuiPopper-arrow': {
            left: 0,
            marginLeft: '-0.71em',
            height: '1em',
            width: '0.71em',
            '&::before': {
              transformOrigin: '100% 100%',
            },
          },
          '&[data-popper-placement*="top"] .MuiPopper-arrow': {
            bottom: 0,
            marginBottom: '-0.71em',
            '&::before': {
              transformOrigin: '100% 0',
            },
          },
          '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
            top: 0,
            marginTop: '-0.71em',
            '&::before': {
              transformOrigin: '0 100%',
            },
          },
        },
      ]}
      modifiers={[
        ...(props.modifiers ?? []),

        {
          name: 'arrow',
          enabled: true,

          options: {
            element: arrowRef,
          },
        },
      ]}
    >
      <Paper
        sx={{
          backgroundColor: 'background.paper',
          maxWidth: 400,

          margin: 0,
          padding: 0,
          position: 'relative',

          '[data-popper-placement*="left"] &': {
            transformOrigin: 'right center',
            marginRight: '14px',
          },
          '[data-popper-placement*="right"] &': {
            transformOrigin: 'left center',
            marginLeft: '14px',
          },
          '[data-popper-placement*="top"] &': {
            transformOrigin: 'center bottom',
            marginBottom: '14px',
          },
          '[data-popper-placement*="bottom"] &': {
            transformOrigin: 'center top',
            marginTop: '14px',
          },
        }}
      >
        <Box
          component="span"
          ref={setArrowRef}
          className="MuiPopper-arrow"
          sx={{
            overflow: 'hidden',
            position: 'absolute',
            width: '1em',
            height: '0.71em' /* = width / sqrt(2) = (length of the hypotenuse) */,
            boxSizing: 'border-box',

            color: 'background.paper',

            '&::before': {
              content: '""',
              margin: 'auto',
              display: 'block',
              width: '100%',
              height: '100%',
              backgroundColor: 'currentColor',
              transform: 'rotate(45deg)',
            },
          }}
        />

        <Box>{props.children}</Box>
      </Paper>
    </Popper>
  );
}
